import type { GtmBrainDoc } from "../api/GtmApi";

export type InfluencerListItem = {
  key: string;
  index: number;
  doc: GtmBrainDoc;
  label: string;
  filled: number;
  total: number;
  isNew: boolean;
};

export type InfluencerFieldId = "name" | "domain" | "linkedinUrl" | "description" | "notes";

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

export function influencerFill(doc: GtmBrainDoc): { filled: number; total: number } {
  const checks = [
    hasText(doc.name),
    hasText(doc.domain),
    hasText(doc.linkedinUrl),
    hasText(doc.description),
    hasText(doc.notes),
  ];
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

export function influencerLabel(doc: GtmBrainDoc, _index: number): string {
  const name = (doc.name || "").trim();
  if (name) return name.length > 72 ? `${name.slice(0, 69)}…` : name;
  return "New reference";
}

/** Stable key — keep in sync with brainToFlow docLeaves ids. */
export function influencerKey(doc: GtmBrainDoc, influencerIndex: number): string {
  return doc.id || `doc-influencer-${influencerIndex}`;
}

export function listInfluencers(docs: GtmBrainDoc[]): InfluencerListItem[] {
  const only = docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => doc.docType === "influencer");
  return only.map(({ doc, index }, influencerIndex) => {
    const fill = influencerFill(doc);
    const label = influencerLabel(doc, influencerIndex);
    return {
      key: influencerKey(doc, influencerIndex),
      index,
      doc,
      label,
      filled: fill.filled,
      total: fill.total,
      isNew: label === "New reference" && fill.filled === 0,
    };
  });
}

export function influencerSections(
  doc: GtmBrainDoc
): Array<{ id: InfluencerFieldId; label: string; body: string }> {
  return [
    { id: "name", label: "Name", body: doc.name || "" },
    { id: "domain", label: "Domain", body: doc.domain || "" },
    { id: "linkedinUrl", label: "LinkedIn URL", body: doc.linkedinUrl || "" },
    { id: "description", label: "Summary", body: doc.description || "" },
    { id: "notes", label: "Positioning notes", body: doc.notes || "" },
  ];
}
